/* hiddenCosmetics — full SVG + recipe data for cursors / trails unlocked
 * via Blackout Pulse. The IDs here MUST match `backend/hidden_cosmetics.py`
 * so claim payloads from /api/blackout/claim resolve to a real design.
 *
 * Each item is generated by combining a shape template with one of the
 * 12 palette colors below. 15 shapes × 12 colors = 180 cursors. 12 trail
 * kinds × 12 colors = 144 trails. ~324 total unlocks per user before the
 * Divine card fallback ever fires.
 */

const COLORS = {
  crimson:   "#ff3b30",
  amber:     "#ffd60a",
  sapphire:  "#3b82f6",
  violet:    "#a78bfa",
  cyan:      "#22d3ee",
  rose:      "#ff2a8e",
  lime:      "#22c55e",
  magenta:   "#d946ef",
  tangerine: "#f59e0b",
  silver:    "#cbd5e1",
  obsidian:  "#1f2937",
  ivory:     "#fef9c3",
};
const COLOR_NAMES = Object.keys(COLORS);

const svgCursor = (svg, hotX = 16, hotY = 16) => {
  const enc = encodeURIComponent(svg).replace(/'/g, "%27").replace(/"/g, "%22");
  return `url("data:image/svg+xml;utf8,${enc}") ${hotX} ${hotY}, auto`;
};
const wrap = (inner) =>
  `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>${inner}</svg>`;

// ---- Cursor shape generators ---------------------------------------------
// Each function returns inner SVG (32x32) using a single hex color.
const SHAPE_FNS = {
  arrow: (c) =>
    `<path d='M4 4 L4 24 L10 18 L14 28 L18 26 L14 17 L22 17 Z' fill='${c}' stroke='#000' stroke-width='1'/>`,
  crosshair: (c) =>
    `<circle cx='16' cy='16' r='11' fill='none' stroke='${c}' stroke-width='1.5'/>` +
    `<line x1='16' y1='2' x2='16' y2='10' stroke='${c}' stroke-width='1.5'/>` +
    `<line x1='16' y1='22' x2='16' y2='30' stroke='${c}' stroke-width='1.5'/>` +
    `<line x1='2' y1='16' x2='10' y2='16' stroke='${c}' stroke-width='1.5'/>` +
    `<line x1='22' y1='16' x2='30' y2='16' stroke='${c}' stroke-width='1.5'/>` +
    `<circle cx='16' cy='16' r='1.5' fill='${c}'/>`,
  eye: (c) =>
    `<path d='M2 16 Q16 5 30 16 Q16 27 2 16Z' fill='${c}' stroke='#000' stroke-width='1'/>` +
    `<circle cx='16' cy='16' r='5' fill='#000'/>` +
    `<circle cx='16' cy='16' r='2' fill='${c}'/>`,
  pyramid: (c) =>
    `<polygon points='16,3 30,28 2,28' fill='${c}' stroke='#000' stroke-width='1'/>` +
    `<circle cx='16' cy='20' r='3.5' fill='#000'/>` +
    `<circle cx='16' cy='20' r='1.3' fill='${c}'/>`,
  ankh: (c) =>
    `<ellipse cx='16' cy='10' rx='5' ry='6' fill='none' stroke='${c}' stroke-width='2'/>` +
    `<line x1='16' y1='16' x2='16' y2='29' stroke='${c}' stroke-width='2.5'/>` +
    `<line x1='8' y1='20' x2='24' y2='20' stroke='${c}' stroke-width='2.5'/>`,
  rune: (c) =>
    `<path d='M8 4 L8 28 M8 4 L20 4 L20 16 L8 16 M20 28 L26 22' stroke='${c}' stroke-width='2.4' fill='none' stroke-linecap='square'/>`,
  diamond: (c) =>
    `<polygon points='16,3 28,16 16,29 4,16' fill='${c}' stroke='#000' stroke-width='1'/>` +
    `<polygon points='16,8 23,16 16,24 9,16' fill='none' stroke='#fff' stroke-width='0.6' opacity='0.6'/>`,
  orb: (c) =>
    `<circle cx='16' cy='16' r='12' fill='${c}'/>` +
    `<circle cx='12' cy='12' r='3' fill='#ffffff' opacity='0.55'/>` +
    `<circle cx='16' cy='16' r='12' fill='none' stroke='#000' stroke-width='0.6'/>`,
  hourglass: (c) =>
    `<path d='M6 4 L26 4 L18 16 L26 28 L6 28 L14 16 Z' fill='${c}' stroke='#000' stroke-width='1'/>` +
    `<line x1='6' y1='4' x2='26' y2='4' stroke='#000' stroke-width='1.4'/>` +
    `<line x1='6' y1='28' x2='26' y2='28' stroke='#000' stroke-width='1.4'/>`,
  key: (c) =>
    `<circle cx='10' cy='10' r='6' fill='none' stroke='${c}' stroke-width='2.5'/>` +
    `<line x1='14' y1='14' x2='28' y2='28' stroke='${c}' stroke-width='2.5'/>` +
    `<line x1='22' y1='22' x2='25' y2='19' stroke='${c}' stroke-width='2.5'/>` +
    `<line x1='25' y1='25' x2='28' y2='22' stroke='${c}' stroke-width='2.5'/>`,
  flame: (c) =>
    `<path d='M16 2 C 22 8, 22 14, 16 18 C 10 14, 10 8, 16 2 Z' fill='${c}'/>` +
    `<path d='M16 12 C 24 18, 24 26, 16 30 C 8 26, 8 18, 16 12 Z' fill='${c}' opacity='0.85'/>` +
    `<ellipse cx='16' cy='22' rx='3' ry='5' fill='#fff' opacity='0.7'/>`,
  moon: (c) =>
    `<path d='M22 6 A 12 12 0 1 0 22 26 A 9 9 0 1 1 22 6 Z' fill='${c}' stroke='#000' stroke-width='0.8'/>`,
  infinity: (c) =>
    `<path d='M4 16 C 4 10, 12 10, 16 16 C 20 22, 28 22, 28 16 C 28 10, 20 10, 16 16 C 12 22, 4 22, 4 16 Z' fill='none' stroke='${c}' stroke-width='3'/>`,
  sigil: (c) =>
    `<circle cx='16' cy='16' r='12' fill='none' stroke='${c}' stroke-width='1.6'/>` +
    `<polygon points='16,5 25,21 7,21' fill='none' stroke='${c}' stroke-width='1.6'/>` +
    `<polygon points='16,27 7,11 25,11' fill='none' stroke='${c}' stroke-width='1.6'/>`,
  third_eye: (c) =>
    `<path d='M2 16 Q16 4 30 16 Q16 28 2 16Z' fill='#000' stroke='${c}' stroke-width='1.4'/>` +
    `<circle cx='16' cy='16' r='6' fill='${c}'/>` +
    `<circle cx='16' cy='16' r='2.5' fill='#000'/>` +
    `<line x1='16' y1='2' x2='16' y2='7' stroke='${c}' stroke-width='1.5'/>`,
};

const SHAPE_LABELS = {
  arrow: "Arrow",
  crosshair: "Crosshair",
  eye: "All-Seeing Eye",
  pyramid: "Pyramid",
  ankh: "Ankh",
  rune: "Rune",
  diamond: "Diamond",
  orb: "Orb",
  hourglass: "Hourglass",
  key: "Skeleton Key",
  flame: "Black Flame",
  moon: "Crescent Moon",
  infinity: "Infinity",
  sigil: "Sigil",
  third_eye: "Third Eye",
};

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const HIDDEN_CURSORS = [];
for (const [shape, fn] of Object.entries(SHAPE_FNS)) {
  for (const colorName of COLOR_NAMES) {
    const hex = COLORS[colorName];
    HIDDEN_CURSORS.push({
      id: `hc_${shape}_${colorName}`,
      name: `${cap(colorName)} ${SHAPE_LABELS[shape]}`,
      hidden: true,
      css: svgCursor(wrap(fn(hex))),
    });
  }
}

// ---- Trail recipe generators --------------------------------------------
const TRAIL_KINDS = {
  flame:   (c) => ({ kind: "glyph", count: 1, glyph: "🔥", color: c, size: 16, life: 700 }),
  ember:   (c) => ({ kind: "blob",  count: 1, color: c, size: 18, life: 650 }),
  ash:     (c) => ({ kind: "dots",  count: 4, color: c, size: 2,  life: 700 }),
  ghost:   (c) => ({ kind: "glyph", count: 1, glyph: "👻", color: c, size: 16, life: 950 }),
  vapor:   (c) => ({ kind: "blob",  count: 1, color: c, size: 26, life: 900 }),
  spark:   (c) => ({ kind: "glyph", count: 1, glyph: "✦", color: c, size: 13, life: 600 }),
  rune:    (c) => ({ kind: "glyph", count: 1, glyph: "ᚱ", color: c, size: 14, life: 800 }),
  eye:     (c) => ({ kind: "glyph", count: 1, glyph: "👁", color: c, size: 14, life: 950 }),
  sigil:   (c) => ({ kind: "glyph", count: 1, glyph: "⛧", color: c, size: 14, life: 900 }),
  static:  (c) => ({ kind: "dots",  count: 6, color: c, size: 1.5, life: 400 }),
  rain:    (c) => ({ kind: "glyph", count: 1, glyph: "│", color: c, size: 14, life: 700 }),
  dust:    (c) => ({ kind: "glyph", count: 1, glyph: "·", color: c, size: 18, life: 1100 }),
};
const TRAIL_LABELS = {
  flame: "Black Flame",
  ember: "Ember Drift",
  ash: "Ash Plume",
  ghost: "Phantom Wisp",
  vapor: "Vapor",
  spark: "Spark Shower",
  rune: "Runic Trail",
  eye: "Watching Eye",
  sigil: "Sigil Drip",
  static: "Dead Static",
  rain: "Black Rain",
  dust: "Stardust",
};

export const HIDDEN_TRAILS = [];
for (const [kind, fn] of Object.entries(TRAIL_KINDS)) {
  for (const colorName of COLOR_NAMES) {
    const hex = COLORS[colorName];
    HIDDEN_TRAILS.push({
      id: `ht_${kind}_${colorName}`,
      name: `${cap(colorName)} ${TRAIL_LABELS[kind]}`,
      hidden: true,
      recipe: fn(hex),
    });
  }
}

export const HIDDEN_CURSOR_IDS = new Set(HIDDEN_CURSORS.map((c) => c.id));
export const HIDDEN_TRAIL_IDS = new Set(HIDDEN_TRAILS.map((t) => t.id));

// Lookup helpers used by ThemeStage / Theme Studio.
export const findHiddenCursor = (id) => HIDDEN_CURSORS.find((c) => c.id === id) || null;
export const findHiddenTrail  = (id) => HIDDEN_TRAILS.find((t) => t.id === id) || null;
